<template>
    <div
        v-if="providers.length > 0"
        class="mt-8 space-y-3"
        data-testid="social-login"
    >
        <div class="relative" role="separator" aria-label="or">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div
                    class="w-full border-t border-taupe-200 dark:border-taupe-700"
                ></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span
                    class="bg-white px-3 text-taupe-500 dark:bg-taupe-900 dark:text-taupe-400"
                    >or</span
                >
            </div>
        </div>

        <zen-button
            v-for="provider in providers"
            :key="provider"
            variant="secondary"
            size="large"
            full-width
            :data-testid="`social-login-${provider}`"
            :disabled="redirecting !== '' && redirecting !== provider"
            :processing="redirecting === provider"
            :processing-label="`Taking you to ${label(provider)}…`"
            @click="start(provider)"
        >
            <template #icon>
                <provider-icon :provider="provider" class="h-5 w-5" />
            </template>
            Continue with {{ label(provider) }}
        </zen-button>

        <p class="text-xs text-taupe-500 dark:text-taupe-400">
            By continuing with {{ providerNames }}, you agree to the
            <a
                href="https://zenmanage.com/legal/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                class="font-medium text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                >Terms of Service</a
            >
            and
            <a
                href="https://zenmanage.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                class="font-medium text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                >Privacy Policy</a
            >. If you don't have an account yet, we'll create one.
        </p>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import ProviderIcon from '@/screens/_components/social/ProviderIcon.vue'
import AuthSocialService from '@/services/auth.social.service'
import {
    knownSocialProviders,
    socialProviderLabel,
} from '@/helpers/social-login'

/**
 * "Continue with GitHub / Google" for the login and sign-up screens.
 *
 * Asks the API which providers are on and draws a button for each one. While
 * the feature is off (or the lookup fails) the API gives no providers and this
 * renders nothing at all, so the screens read exactly as they did before. A
 * click navigates the whole window to the API, which sends the browser on to
 * the provider; the way back lands on the login screen with the outcome.
 */
const providers = ref(/** @type {string[]} */ ([]))
// '' is "none": no provider has been clicked yet.
const redirecting = ref('')

const label = socialProviderLabel

const providerNames = computed(() =>
    providers.value.map((provider) => label(provider)).join(' or ')
)

function start(provider) {
    if (redirecting.value !== '') {
        return
    }

    redirecting.value = provider
    globalThis.window.location.assign(AuthSocialService.redirectUrl(provider))
}

// Coming back to this page with the browser's Back button can restore it from
// the back/forward cache exactly as it was left, spinner and all. Nothing is
// loading any more, so put the buttons back.
function onPageShow(event) {
    if (event.persisted) {
        redirecting.value = ''
    }
}

onMounted(async () => {
    globalThis.window.addEventListener('pageshow', onPageShow)

    try {
        providers.value = knownSocialProviders(
            await AuthSocialService.providers()
        )
    } catch {
        // The password form still works without the buttons.
        providers.value = []
    }
})

onBeforeUnmount(() => {
    globalThis.window.removeEventListener('pageshow', onPageShow)
})
</script>
